'use client';

import { Box, Container, SxProps, Typography } from '@mui/material';
import Logo from '@/../public/logo_mobile.png';
import LogoNormal from '@/../public/logo_normal.png';
import HomeImage from '@/../public/home.jpg';
import { InputZipcode } from './InputZipcode';
import NextLink from '@/components/link/NextLink';
import { MuiButton } from '@/components/button/MuiButton';
import { useLogic } from './logic';

const outlinedBtnStyle: SxProps = {
  bgcolor: '#FFFFFF',
  '&:hover': {
    bgcolor: `rgba(62, 201, 190, 0.2)`,
    color: 'primary.dark',
    borderColor: 'primary.dark',
  },
};
const textLinkStyle: SxProps = {
  p: 0.25,
  fontSize: '16px',
  bgcolor: '#FFF',
  boxShadow: `0px 0px 8px 8px #FFF`,
  borderRadius: 0.5,
  '&:hover': { color: 'primary.dark' },
};

type Props = {
  search: () => void;
  isPublic: boolean;
};

export function HeroArea({ search, isPublic }: Props) {
  const { onClickStation, onClickHospital } = useLogic();

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center 25%',
          backgroundImage: `linear-gradient(270deg, rgba(255, 255, 255, 0.00) 40%, #FFF 70%), url(${HomeImage.src})`,
          width: `calc(100vw - 10px)`,
          height: '424px',
          margin: `0 calc(50% - 50vw)`,
          '@media (max-width: 600px)': {
            width: `calc(100vw)`,
            backgroundPosition: '80% 20%',
            height: '370px',
            backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.00) 45%, #FFF 75%), url(${HomeImage.src})`,
          },
        }}
      >
        {/* PC表示のコンテンツ */}
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            '@media (max-width: 600px)': { display: 'none' },
          }}
        >
          <Box
            component="img"
            src={LogoNormal.src}
            alt="logo"
            sx={{
              height: '155px',
              width: '275px',
              objectFit: 'cover',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="JP/Heading/xl/Bold/Tight" component="p">
              近くの看護・介護サービスを探す
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InputZipcode search={search} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <NextLink
                href={`/${isPublic ? 'public' : 'app'}/stations/search`}
                underline="always"
                sx={textLinkStyle}
                onClick={onClickStation}
              >
                看護・介護サービス詳細検索へ
              </NextLink>

              <NextLink
                href={`/${
                  isPublic ? 'public' : 'app'
                }/stations/search?searchTarget=hospital`}
                underline="always"
                sx={textLinkStyle}
                onClick={onClickHospital}
              >
                医療機関詳細検索へ
              </NextLink>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <MuiButton
              variant="outlined"
              color="primary"
              onClick={() =>
                window.open(process.env.NEXT_PUBLIC_GUIDE_URL, '_blank')
              }
              sx={outlinedBtnStyle}
            >
              けあログっとの使い方
            </MuiButton>
          </Box>
        </Container>
      </Box>

      {/* モバイル表示のコンテンツ */}
      <Container
        sx={{
          display: 'none',
          '@media (max-width: 600px)': {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 0,
            px: 0,
            mt: '-145px',
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
        <Box
          component="img"
          src={Logo.src}
          alt="logo"
          sx={{
            height: '84px',
            width: '200px',
            objectFit: 'contain',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="JP/Heading/xl/Bold/Tight"
            component="p"
            sx={{ fontSize: '22px' }}
          >
            近くの看護・介護サービスを探す
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <InputZipcode search={search} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <NextLink
              href={`/${isPublic ? 'public' : 'app'}/stations/search`}
              underline="always"
              sx={textLinkStyle}
              onClick={onClickStation}
            >
              看護・介護サービス詳細検索へ
            </NextLink>

            <NextLink
              href={`/${
                isPublic ? 'public' : 'app'
              }/stations/search?searchTarget=hospital`}
              underline="always"
              sx={textLinkStyle}
              onClick={onClickHospital}
            >
              医療機関詳細検索へ
            </NextLink>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <MuiButton
            variant="outlined"
            color="primary"
            onClick={() =>
              window.open(process.env.NEXT_PUBLIC_GUIDE_URL, '_blank')
            }
            sx={outlinedBtnStyle}
          >
            けあログっとの使い方
          </MuiButton>
        </Box>
      </Container>
    </Box>
  );
}
